import React, { useEffect, useState } from 'react';
import { Loader2, Trophy } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getLeaderboard, LeaderboardResponse, LeaderboardEntry } from '../../services/leaderboardService';
import { LeaderboardRow } from './LeaderboardRow';
import { LeaderboardMobileCard } from './LeaderboardMobileCard';
import { toastHelper } from '../../utils/toastHelper';
import './Leaderboard.css';

type Period = 'monthly' | 'yearly' | 'overall';
type ViewType = 'top_performance';

const PERIOD_LABELS: Record<Period, string> = {
  monthly: 'This Month',
  yearly: 'This Year',
  overall: 'All Time',
};

const VIEW_TYPE_LABELS: Record<ViewType, string> = {
  top_performance: 'Top Performance',
};

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('monthly');
  const [selectedViewType, setSelectedViewType] = useState<ViewType>('top_performance');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const handleToggleRow = (userId: string) => {
    setExpandedRow(expandedRow === userId ? null : userId);
  };

  const fetchLeaderboard = async (period: Period) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLeaderboard(period);
      setLeaderboard(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load leaderboard';
      setError(errorMessage);
      toastHelper.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard(selectedPeriod);
  }, [selectedPeriod]);

  const formatRank = (rank: number) => {
    const suffix = ['th', 'st', 'nd', 'rd'];
    const v = rank % 100;
    return rank + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
  };

  return (
    <div className="leaderboard-container p-3 sm:p-6 max-w-6xl mx-auto">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Trophy className="h-5 w-5 sm:h-6 sm:w-6" />
                Leaderboard
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                See who's leading the pack with the most uploads
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {/* View Type Selector */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={selectedViewType === 'top_performance' ? 'default' : 'outline'}
              onClick={() => setSelectedViewType('top_performance')}
              className="flex-1 text-sm sm:text-base"
            >
              {VIEW_TYPE_LABELS.top_performance}
            </Button>
          </div>

          {/* Period Selector */}
          <div className="flex gap-2 mb-6 flex-wrap sm:flex-nowrap">
            <Button
              variant={selectedPeriod === 'monthly' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('monthly')}
              className="flex-1 min-w-[100px] text-xs sm:text-sm"
            >
              {PERIOD_LABELS.monthly}
            </Button>
            <Button
              variant={selectedPeriod === 'yearly' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('yearly')}
              className="flex-1 min-w-[100px] text-xs sm:text-sm"
            >
              {PERIOD_LABELS.yearly}
            </Button>
            <Button
              variant={selectedPeriod === 'overall' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('overall')}
              className="flex-1 min-w-[100px] text-xs sm:text-sm"
            >
              {PERIOD_LABELS.overall}
            </Button>
          </div>

          {/* Current User Stats */}
          {leaderboard && leaderboard.current_user_rank && (
            <div className="mb-6 p-3 sm:p-4 bg-muted rounded-lg border">
              <div className="flex items-center justify-between flex-wrap gap-4 sm:flex-nowrap">
                <div className="flex-1 min-w-[100px]">
                  <p className="text-xs sm:text-sm font-medium">Your Position</p>
                  <p className="text-xl sm:text-2xl font-bold">
                    {formatRank(leaderboard.current_user_rank)}
                  </p>
                </div>
                <div className="text-right flex-1 min-w-[100px]">
                  <p className="text-xs sm:text-sm text-muted-foreground">Your Uploads</p>
                  <p className="text-xl sm:text-2xl font-bold">{leaderboard.current_user_upload_count}</p>
                </div>
                {leaderboard.uploads_behind_next !== null && leaderboard.uploads_behind_next > 0 && (
                  <div className="text-right flex-1 min-w-[100px]">
                    <p className="text-xs sm:text-sm text-muted-foreground">Uploads Behind Next</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-500">
                      {leaderboard.uploads_behind_next}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-12">
              <p className="text-destructive">{error}</p>
              <Button
                variant="outline"
                onClick={() => fetchLeaderboard(selectedPeriod)}
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Leaderboard with Expandable Rows */}
          {!loading && !error && leaderboard && (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table className="min-w-[600px] table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Rank</TableHead>
                      <TableHead className="min-w-[300px]">User</TableHead>
                      <TableHead className="text-right w-24">Uploads</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboard.entries.length === 0 ? (
                      <TableRow>
                        <td colSpan={3} className="text-center py-8 text-muted-foreground">
                          No users found
                        </td>
                      </TableRow>
                    ) : (
                      leaderboard.entries.map((entry: LeaderboardEntry) => (
                        <LeaderboardRow
                          key={entry.user_id}
                          entry={entry}
                          isExpanded={expandedRow === entry.user_id}
                          onToggle={() => handleToggleRow(entry.user_id)}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {leaderboard.entries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  leaderboard.entries.map((entry: LeaderboardEntry) => (
                    <LeaderboardMobileCard
                      key={entry.user_id}
                      entry={entry}
                      isExpanded={expandedRow === entry.user_id}
                      onToggle={() => handleToggleRow(entry.user_id)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Leaderboard;
